import i18n from "i18next";
import { redirectToCentralLogin } from "@thuthi/web-shared/central-auth";
import api from "./Api";
import subscriptionManager from "./SubscriptionManager";
import prefs from "./Prefs";
import { UnauthorizedError } from "./errors";
import config from "./config";
import type { Account, AccountNotificationPrefs } from "./types";

type AccountListener = (account: Account) => void;

/**
 * 내 구독·설정·발행 토큰 — GET /api/v2/me + /api/v2/tokens 를 예전 Account 모양으로 묶어 화면에 준다.
 * 다른 기기에서 바꾸면 SSE `sync` 이벤트가 오고(ConnectionManager) sync() 로 다시 읽는다.
 * 예전 ntfy 계정 기능(비밀번호·가입·예약·결제·전화·이메일·토큰 연장)은 백엔드가 없어 뺐다.
 */
class AccountApi {
  listener: AccountListener | null = null;

  registerListener(listener: AccountListener) {
    this.listener = listener;
  }

  resetListener() {
    this.listener = null;
  }

  async get(): Promise<Account> {
    const [me, tokens] = await Promise.all([api.me(), api.tokens()]);
    const meta = me.prefs as { language?: string; notification?: AccountNotificationPrefs };
    const account: Account = {
      username: me.user,
      role: me.role,
      language: meta.language,
      notification: meta.notification,
      subscriptions: me.subscriptions.map((s) => ({
        base_url: config.base_url,
        topic: s.topic,
        display_name: s.display_name,
        muted_until: s.muted_until,
        icon: s.icon,
      })),
      tokens: tokens.map((t) => ({ ...t, expires: t.expires || undefined })),
    };
    this.listener?.(account);
    return account;
  }

  /** 발행 토큰 — expires 는 지금부터 초(0 = 만료 없음) */
  async createToken(label: string, expires: number) {
    const token = await api.createToken(label, expires > 0 ? Math.floor(Date.now() / 1000) + expires : 0);
    await this.sync();
    return token;
  }

  async deleteToken(token: string) {
    await api.deleteToken(token);
    await this.sync();
  }

  /** 서버 prefs 병합은 한 단계뿐이라 notification 안쪽은 여기서 합쳐 보낸다 */
  async updateSettings(payload: Partial<Account>) {
    const body: Record<string, unknown> = { ...payload };
    if (payload.notification) {
      const current = ((await api.me()).prefs.notification ?? {}) as AccountNotificationPrefs;
      body.notification = { ...current, ...payload.notification };
    }
    await api.patchPrefs(body);
  }

  async addSubscription(_baseUrl: string, topic: string) {
    await api.putSubscription(topic, {});
  }

  async updateSubscription(_baseUrl: string, topic: string, payload: Record<string, unknown>) {
    await api.putSubscription(topic, payload);
  }

  async deleteSubscription(_baseUrl: string, topic: string) {
    await api.deleteSubscription(topic);
  }

  async sync() {
    try {
      console.log(`[AccountApi] Syncing account`);
      const account = await this.get();
      if (account.language) {
        await i18n.changeLanguage(account.language);
      }
      if (account.notification) {
        if (account.notification.sound) {
          await prefs.setSound(account.notification.sound);
        }
        if (account.notification.delete_after) {
          await prefs.setDeleteAfter(account.notification.delete_after);
        }
      }
      if (account.subscriptions) {
        await subscriptionManager.syncFromRemote(account.subscriptions, []);
      }
      return account;
    } catch (e) {
      console.log(`[AccountApi] Error fetching account`, e);
      if (e instanceof UnauthorizedError) {
        redirectToCentralLogin();
      }
      return undefined;
    }
  }
}

// Maps to the server role (users.role)
export const Role = {
  ADMIN: "admin",
  USER: "user",
};

const accountApi = new AccountApi();
export default accountApi;
